import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  MAX_ROWS_INCLUDE, MAX_ROW_LABEL, rowChoices, rowKey, rowsButtonText, withRows,
} from '../../utils/lionAutoRows';

// The Rows filter of a Lion Auto group (criteria.rowsInclude): a button that
// names the pick ("All rows", "Rows A, B, C", "12 rows") and opens a panel
// listing the rows that exist in the group's sections — ours and the
// market's, each with how many listings sit in it, in natural order — with a
// search box, Select all / Clear for the rows shown and a checkbox per row.
// A picked row no listing sits in now stays listed ("no listings") so it can
// be unticked. A click outside, Esc (focus back on the button) or tabbing
// out closes the panel.

const NO_PICKS = [];
const countText = (c) => (c.missing ? 'no listings' : `${c.own} yours · ${c.comps} comp${c.comps === 1 ? '' : 's'}`);

/**
 * @param {string[]} props.picked  criteria.rowsInclude (empty = every row)
 * @param {object|null} props.options  rowOptionsFrom() for the group's sections, null while the listings load
 * @param {boolean} props.hasSections  the group has sections (else there are no rows to offer)
 * @param {Function} props.onChange  (nextPicked) => void
 */
function LaRowsPicker({ picked, options, hasSections, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const searchRef = useRef(null);
  const panelId = useId();

  const list = Array.isArray(picked) ? picked : NO_PICKS;
  const choices = useMemo(() => rowChoices(options, list), [options, list]);
  const q = rowKey(query);
  const shown = q ? choices.filter(c => c.key.includes(q)) : choices;
  const full = list.length >= MAX_ROWS_INCLUDE;
  const addable = shown.filter(c => !c.picked && !c.tooLong);
  const removable = shown.filter(c => c.picked);
  const loading = hasSections && !options;

  // The cursor goes to the search box when the panel opens
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // A click outside closes it
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggleOpen = () => {
    if (!open) setQuery('');
    setOpen(o => !o);
  };
  const onKeyDown = (e) => {
    if (e.key !== 'Escape' || !open) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    buttonRef.current?.focus();
  };
  // Focus moving out (Tab) closes it; a click inside on no control (focus
  // going nowhere) keeps it open
  const onBlur = (e) => {
    if (open && e.relatedTarget && wrapRef.current && !wrapRef.current.contains(e.relatedTarget)) setOpen(false);
  };

  let empty = null;
  if (shown.length === 0 && !loading) {
    if (q && choices.length) empty = `No row matches “${query.trim()}”.`;
    else if (!hasSections) empty = 'Pick sections first: their rows show here.';
    else empty = 'No listing in these sections has a row label.';
  }
  const blankOwn = options?.blankOwn || 0;
  const blankComps = options?.blankComps || 0;

  return (
    <div className="la-rows" ref={wrapRef} onKeyDown={onKeyDown} onBlur={onBlur}>
      <button
        type="button"
        ref={buttonRef}
        className={`form-input form-input-sm la-rows-toggle${list.length ? ' on' : ''}`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        title={list.length ? `Only rows ${list.join(', ')} match` : 'Every row matches; click to pick rows'}
        onClick={toggleOpen}
      >
        <span className="la-rows-toggle-text">{rowsButtonText(list)}</span>
        <span className="la-rows-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="la-rows-pop" id={panelId} role="group" aria-label="Rows to match">
          <input
            ref={searchRef}
            type="text"
            className="form-input form-input-sm"
            value={query}
            placeholder="Search rows"
            aria-label="Search rows"
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="la-rows-links">
            <button
              type="button"
              className="la-link"
              disabled={addable.length === 0 || full}
              title={q ? 'Tick every row the search shows' : 'Tick every row in the list'}
              onClick={() => onChange(withRows(list, addable.map(c => c.label), true))}
            >
              Select all
            </button>
            <button
              type="button"
              className="la-link"
              disabled={removable.length === 0}
              title={q ? 'Untick every row the search shows' : 'Untick every row: every row matches'}
              onClick={() => onChange(withRows(list, removable.map(c => c.label), false))}
            >
              Clear
            </button>
            <span className="la-muted la-push-right">{list.length ? `${list.length} ticked` : 'none ticked = every row'}</span>
          </div>
          {loading && <div className="la-rows-empty">Loading your listings and the market…</div>}
          <div className="la-rows-list">
            {shown.map(c => {
              const disabled = !c.picked && (c.tooLong || full);
              return (
                <label
                  key={c.key}
                  className={`la-rows-item${c.missing ? ' missing' : ''}${disabled ? ' disabled' : ''}`}
                  title={c.tooLong ? `Row labels over ${MAX_ROW_LABEL} characters can't be picked` : (c.missing ? 'Ticked, but no listing of yours or the market sits in this row now' : undefined)}
                >
                  <input
                    type="checkbox"
                    checked={c.picked}
                    disabled={disabled}
                    onChange={() => onChange(withRows(list, [c.label], !c.picked))}
                  />
                  <span className="la-rows-label">{c.label}</span>
                  <span className="la-rows-count">{countText(c)}</span>
                </label>
              );
            })}
            {empty && <div className="la-rows-empty">{empty}</div>}
          </div>
          {full && <div className="la-hint warn">{MAX_ROWS_INCLUDE} rows at most.</div>}
          {(blankOwn > 0 || blankComps > 0) && (
            <div className="la-hint">
              No row label: {blankOwn} yours · {blankComps} comp{blankComps === 1 ? '' : 's'}. They match only while nothing is ticked.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LaRowsPicker;
